import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportsApi, downloadAuthed } from '../api';
import StatCard from '../components/StatCard';
import PageHeader from '../components/PageHeader';
import BarChart from '../components/BarChart';
import FulfilmentDigestPanel from '../components/FulfilmentDigestPanel';
import SyncFailedBanner from '../components/SyncFailedBanner';
import { Loader2, TrendingDown, ChevronRight, Download } from 'lucide-react';

export default function AdminOverview() {
  const { user, theme } = useAuth();
  const [data, setData] = useState(null);

  const canDigest = ['SuperAdmin', 'AllIndiaManager', 'Admin'].includes(user?.role);
  const canDownloadPending = ['SuperAdmin', 'AllIndiaManager', 'Admin', 'Warehouse', 'Logistics', 'RetailManager'].includes(user?.role);

  useEffect(() => {
    reportsApi.overview().then(setData).catch(() => {});
  }, []);

  if (!data) return <div className="p-10 text-center text-[#7a6f5c]"><Loader2 className="animate-spin inline" /></div>;

  return (
    <div>
      <PageHeader
        title="Program overview"
        subtitle="All EBOs · all SKUs"
        actions={
          canDownloadPending && (
            <button
              data-testid="download-pending-btn"
              onClick={() => downloadAuthed(reportsApi.pendingRequisitionsExportUrl(), `pending-requisitions-${new Date().toISOString().slice(0,10)}.xlsx`)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px] font-medium"
              style={{ background: theme.primary }}
              title="Download all pending requisitions (Requested / Fulfilled / Shipped / Held) with EBO + article-level details"
            >
              <Download size={13}/> Download pending requisitions
            </button>
          )
        }
      />

      {['SuperAdmin', 'Admin'].includes(user?.role) && <SyncFailedBanner />}

      <div className="flex flex-wrap gap-4 mb-6">
        <StatCard label="Requisition lines (all time)" value={data.totalLines} />
        <StatCard label="Avg. days to fulfil" value={data.avgDaysToFulfil} valueColor="#B78140" />
        <StatCard label="SKU × EBO pairs in cooldown" value={data.cooldownPairs} valueColor={theme.primary} />
        <StatCard label="Active EBO branches" value={data.activeBranches} />
        <StatCard label="Live SKUs" value={data.liveSkus} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <div className="fm-card p-5">
          <h3 className="text-[15px] font-semibold text-[#232323] mb-3">Lines by status</h3>
          <BarChart data={data.linesByStatus} primary={theme.primary} height={220} />
        </div>
        <div className="fm-card p-5">
          <h3 className="text-[15px] font-semibold text-[#232323] mb-3">Most requested testers</h3>
          <BarChart data={data.mostRequestedTesters} horizontal secondary="#4D7C5F" height={220} />
        </div>
      </div>

      <div className="fm-card p-5" data-testid="fulfillment-insights">
        <div className="flex items-center gap-2 mb-3">
          <TrendingDown size={16} style={{ color: theme.primary }} />
          <h3 className="text-[15px] font-semibold text-[#232323]">Fulfillment lead time</h3>
          <span className="ml-auto text-[12px] text-[#7a6f5c]">
            Based on <span className="font-semibold text-[#232323]" data-testid="lead-time-sample">{data.fulfilledCount || 0}</span> fulfilled requisitions ·
            <span className="ml-1">avg <span className="font-semibold text-[#232323]" data-testid="lead-time-avg">{data.avgDaysToFulfil ?? 0}</span> days</span>
            <span className="ml-1">· median <span className="font-semibold text-[#232323]" data-testid="lead-time-median">{data.medianDaysToFulfil ?? 0}</span> days</span>
          </span>
        </div>
        {(data.slowestBranches || []).length === 0 ? (
          <div className="text-[13px] text-[#7a6f5c] py-4">No fulfilled requisitions yet — the top-5 slowest branches will appear here once fulfillment data is available.</div>
        ) : (
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3]">
                <th className="px-3 py-2 font-medium">Rank</th>
                <th className="px-3 py-2 font-medium">Branch</th>
                <th className="px-3 py-2 font-medium text-center">Fulfilled requisitions</th>
                <th className="px-3 py-2 font-medium text-right pr-6">Avg. days to fulfil</th>
              </tr>
            </thead>
            <tbody>
              {data.slowestBranches.map((b, i) => (
                <tr
                  key={b.branch}
                  className="border-t border-[#EFEADC] hover:bg-[#F7F4EA] cursor-pointer transition-colors"
                  data-testid={`slow-branch-${b.branch}`}
                  onClick={() => window.location.assign(`/app/requisitions?ebo=${encodeURIComponent(b.branch)}`)}
                >
                  <td className="px-3 py-2.5 text-[#7a6f5c]">#{i + 1}</td>
                  <td className="px-3 py-2.5 font-mono">
                    <Link
                      to={`/app/requisitions?ebo=${encodeURIComponent(b.branch)}`}
                      className="underline"
                      style={{ color: theme.primary }}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`slow-branch-link-${b.branch}`}
                    >{b.branch}</Link>
                  </td>
                  <td className="px-3 py-2.5 text-center">{b.reqs}</td>
                  <td className="px-3 py-2.5 text-right pr-6 font-semibold" style={{ color: b.avgDays > 5 ? '#B84A4A' : '#232323' }}>
                    <span className="inline-flex items-center gap-1">{b.avgDays} d <ChevronRight size={13} className="text-[#7a6f5c]" /></span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {canDigest && <FulfilmentDigestPanel theme={theme} isSuper={user?.role === 'SuperAdmin'} />}
    </div>
  );
}
